<script setup lang="ts">
import { GenericObject, useForm } from 'vee-validate';
import { inject, onMounted, provide, ref, shallowRef, watch } from 'vue';

import type {
  FormEmits,
  FormPayload,
  FormProps,
  FormSlots,
} from './Form.vue.d';
import Button from '../button/Button.vue';
import Checkbox from '../checkbox/Checkbox.vue';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = withDefaults(defineProps<FormProps>(), {
  cancelBtnLabel: 'Batal',
  clearBtnLabel: 'Bersihkan Field',
  saveBtnLabel: 'Simpan',
  submitBtnLabel: 'Simpan',
  resetAfterSubmit: true,
  cancelBtnSeverity: 'secondary',
  saveBtnSeverity: 'success',
  submitBtnSeverity: 'success',
  stayCheckboxLabel: 'Tetap di halaman ini',
  validatorMessage: 'Please input all required field!',
  validateOnSave: false,
});

const emit = defineEmits<FormEmits>();
defineSlots<FormSlots>();

onMounted(() => {
  if (fieldsWrapper.value) {
    const colCount = props.columnPerRow ?? 1;
    fieldsWrapper.value.style.gridTemplateColumns = `repeat(${colCount}, minmax(0, 1fr))`;

    setOuterFieldsWrapperHeight();
  }
});

const Preset = inject<Record<string, any>>('preset', {}).form;

const {
  handleSubmit,
  values,
  resetForm,
  errors,
  resetField,
  setValues,
  setFieldValue,
  setErrors,
} = useForm();

const stayAfterSubmit = shallowRef<boolean>(false);

const formElement = ref<HTMLFormElement>();
const showValidator = shallowRef<boolean>(false);
const validated = shallowRef<boolean>(false);
const fieldsKey = shallowRef<number>(0);
const footer = ref<HTMLDivElement>();
const isSubmitting = shallowRef<boolean>(false); // Accessed by inputtext
const isSaveClicked = shallowRef<boolean>(false);
const isSubmitClicked = shallowRef<boolean>(false);

const fieldsWrapper = ref<HTMLDivElement | null>(null);
const outerFieldsWrapper = ref<HTMLDivElement | null>(null);

provide('isSubmitting', isSubmitting);

const setOuterFieldsWrapperHeight = (): void => {
  if (footer.value) {
    const footerHeight = footer.value?.offsetHeight;

    if (outerFieldsWrapper.value)
      outerFieldsWrapper.value.style.height = `calc(100% + ${
        footerHeight + 20
      }px)`;
  }
};

const onSubmitClicked = (): void => {
  isSubmitting.value = true;
  isSubmitClicked.value = true;
  if (!validated.value) showValidator.value = true;
};

const onSaveClicked = (): void => {
  isSubmitting.value = true;
  isSaveClicked.value = true;

  if (props.validateOnSave) {
    if (!validated.value) showValidator.value = true;
    submit();
  } else {
    save();
  }
};

const submit = handleSubmit(
  (formValues) => {
    if (!props.invalid) {
      const formattedValues = trimValues(formValues);

      const payload: FormPayload = {
        stayAfterSubmit: stayAfterSubmit.value,
        formValues: formattedValues,
      };

      if (isSaveClicked.value) emit('save', payload);
      else emit('submit', payload);
      showValidator.value = false;
      isSaveClicked.value = false;

      if (props.resetAfterSubmit) {
        fieldsKey.value += 1;
        resetForm();
      }
    }

    isSubmitting.value = false;
  },
  () => {
    isSubmitting.value = false;
  },
);

const save = (): void => {
  const formattedValues = trimValues(values);

  const payload: FormPayload = {
    stayAfterSubmit: stayAfterSubmit.value,
    formValues: formattedValues,
  };

  emit('save', payload);
  isSubmitting.value = false;
};

const clearField = (): void => {
  resetForm();
  fieldsKey.value++;
  emit('clear');
  showValidator.value = false;
  isSubmitClicked.value = false;
};

const trimValues = (formValues: GenericObject): GenericObject => {
  const formattedValues = formValues;
  const keys = Object.keys(formattedValues);

  for (const key of keys) {
    if (typeof formattedValues[key] === 'string') {
      formattedValues[key] = formattedValues[key].trim();
    }
  }

  return formattedValues;
};

watch(
  values,
  () => {
    isSubmitClicked.value = false;
  },
  { deep: true },
);

defineExpose({
  stayAfterSubmit,
  submit,
  onSubmitClicked,
  onSaveClicked,
  showValidator,
  formElement,
  clearField,
  resetField,
  errors,
  setOuterFieldsWrapperHeight,
  setValues,
  setFieldValue,
  setErrors,
  values,
  isSubmitClicked,
});
</script>

<template>
  <form
    ref="formElement"
    v-bind="Preset?.root({ props })"
    @input="showValidator = false"
    @submit.prevent="submit"
  >
    <div ref="outerFieldsWrapper">
      <div ref="fieldsWrapper" v-bind="Preset?.['fields-wrapper']">
        <slot :key="fieldsKey" :form-values="values" name="fields" />
      </div>
    </div>

    <div ref="footer" v-if="!hideFooter" v-bind="Preset?.footer">
      <div v-if="!hideStayCheckbox" v-bind="Preset?.staycheckbox">
        <Checkbox v-model="stayAfterSubmit" :label="stayCheckboxLabel" />
      </div>

      <div id="footer-actions-wrapper" v-bind="Preset?.['action-buttons']">
        <ValidatorMessage
          v-show="showValidator || props.invalid"
          v-bind="Preset?.['validator-message']"
          :message="validatorMessage"
        />

        <div v-bind="Preset?.['button-wrapper']">
          <Button
            v-if="props.buttonsTemplate?.includes('cancel')"
            v-bind="Preset?.['cancel-button']"
            :label="cancelBtnLabel"
            :severity="cancelBtnSeverity"
            @click="$emit('cancel')"
            data-test="cancel-button"
            text
            type="button"
          />

          <Button
            v-if="props.buttonsTemplate?.includes('clear')"
            v-bind="Preset?.['clear-button']"
            :label="clearBtnLabel"
            @click="clearField"
            text
            type="button"
          />

          <Button
            v-if="props.buttonsTemplate?.includes('save')"
            v-bind="Preset?.['save-button']"
            :label="saveBtnLabel"
            :severity="saveBtnSeverity"
            @click="onSaveClicked"
            outlined
            type="button"
          />

          <Button
            v-if="props.buttonsTemplate?.includes('submit')"
            v-bind="Preset?.['submit-button']"
            :disabled="disableSubmit"
            :label="submitBtnLabel"
            :severity="submitBtnSeverity"
            @click="onSubmitClicked"
            type="submit"
          />
        </div>
      </div>
    </div>
  </form>
</template>
